import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, tap, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '../models/api-response.model';
import {
  AuthUser,
  EmpresaAmbiguaError,
  EscolherEmpresaPayload,
  LoginEmpresaAmbiguaError,
  LoginPayload,
  LoginResponse,
} from '../models/auth.model';

export type {
  AuthUser,
  EmpresaAmbiguaError,
  EmpresaCandidata,
  EscolherEmpresaPayload,
  LoginEmpresaAmbiguaError,
  LoginPayload,
  LoginResponse,
} from '../models/auth.model';

/**
 * Type guard para o `409 EMPRESA_AMBIGUA` de `POST /api/v1/auth/login`.
 *
 * O 409 NAO e uma falha de rede generica — e fluxo de negocio (F7-00 Fase 4).
 * O `ErrorInterceptor` so anexa `apiError` e repassa o erro (nao decide nada);
 * cabe ao chamador checar este guard ANTES de tratar o erro como falha comum
 * (ex.: nunca deve virar toast generico "nao foi possivel entrar").
 */
export function isEmpresaAmbiguaError(
  err: unknown,
): err is HttpErrorResponse & { error: LoginEmpresaAmbiguaError } {
  return (
    err instanceof HttpErrorResponse &&
    err.status === 409 &&
    (err.error as Partial<EmpresaAmbiguaError> | null)?.codigo === 'EMPRESA_AMBIGUA'
  );
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly accessTokenKey = 'manager-access-token';
  private readonly refreshTokenKey = 'manager-refresh-token';
  private readonly userKey = 'manager-user';
  /**
   * Dica de empresa (F7-00 Fase 4/5) — guarda SO o `ref` opaco escolhido numa
   * desambiguacao anterior. Nunca identificador, nome ou e-mail: nada que
   * identifique a pessoa fica no navegador (spec §4.3). Ponto unico de
   * leitura/escrita — nao duplicar esta chave em outro service/component.
   */
  private readonly empresaRefHintKey = 'manager-empresa-ref-hint';
  private readonly baseUrl = environment.apiBaseUrl;
  private accessToken: string | null = null;
  private refreshTokenValue: string | null = null;
  private currentUser: AuthUser | null = null;

  constructor(private http: HttpClient) {
    // Migration: clear old sessionStorage keys if present
    if (sessionStorage.getItem('manager-token')) {
      sessionStorage.removeItem('manager-token');
      sessionStorage.removeItem('manager-user');
    }

    this.accessToken = localStorage.getItem(this.accessTokenKey);
    this.refreshTokenValue = localStorage.getItem(this.refreshTokenKey);
    const rawUser = localStorage.getItem(this.userKey);
    if (rawUser) {
      try { this.currentUser = JSON.parse(rawUser) as AuthUser; }
      catch { this.currentUser = null; localStorage.removeItem(this.userKey); }
    }
  }

  login(payload: LoginPayload): Observable<LoginResponse> {
    return this.http.post<ApiResponse<LoginResponse>>(`${this.baseUrl}/api/v1/auth/login`, payload).pipe(
      map(response => {
        if (!response?.conteudo) throw new Error('Resposta de login inválida');
        return response.conteudo;
      }),
      tap(response => {
        const token = response.accessToken || response.tokenAcesso;
        this.setToken(token);
        this.setRefreshToken(response.refreshToken);
        this.setUser(response.usuario ?? null);
      })
    );
  }

  /**
   * Passo 2 do login ambiguo (F7-00 Fase 4) — `POST /api/v1/auth/login/empresa`.
   * Troca o `desambiguacaoToken` do 409 pela empresa escolhida; a senha NAO
   * trafega de novo. O token e de uso unico — queimado nesta chamada mesmo se
   * `empresaRef` estiver incorreta (nao ha "tentar de novo" com o mesmo token).
   */
  escolherEmpresa(payload: EscolherEmpresaPayload): Observable<LoginResponse> {
    return this.http
      .post<ApiResponse<LoginResponse>>(`${this.baseUrl}/api/v1/auth/login/empresa`, payload)
      .pipe(
        map(response => {
          if (!response?.conteudo) throw new Error('Resposta de login inválida');
          return response.conteudo;
        }),
        tap(response => {
          const token = response.accessToken || response.tokenAcesso;
          this.setToken(token);
          this.setRefreshToken(response.refreshToken);
          this.setUser(response.usuario ?? null);
        })
      );
  }

  /** Guarda a dica de empresa (`empresas[].ref` de uma desambiguacao resolvida). */
  setEmpresaRefHint(ref: string): void {
    localStorage.setItem(this.empresaRefHintKey, ref);
  }

  getEmpresaRefHint(): string | null {
    return localStorage.getItem(this.empresaRefHintKey);
  }

  /** Limpa a dica — chamado no logout e em "trocar empresa". */
  clearEmpresaRefHint(): void {
    localStorage.removeItem(this.empresaRefHintKey);
  }

  refreshAccessToken(): Observable<{ accessToken: string; refreshToken: string; expiraEm: number }> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return throwError(() => new Error('No refresh token'));

    return this.http.post<ApiResponse<{ accessToken: string; refreshToken: string; expiraEm: number }>>(
      `${this.baseUrl}/api/v1/auth/refresh`,
      { refreshToken }
    ).pipe(
      map(r => r.conteudo),
      tap(response => {
        this.setToken(response.accessToken);
        this.setRefreshToken(response.refreshToken);
      })
    );
  }

  logout(): Observable<void> {
    const refreshToken = this.getRefreshToken();
    const logout$ = refreshToken
      ? this.http.post<void>(`${this.baseUrl}/api/v1/auth/logout`, { refreshToken })
      : of(undefined as void);

    return logout$.pipe(
      finalize(() => this.clearAll())
    );
  }

  setToken(token: string): void {
    this.accessToken = token;
    localStorage.setItem(this.accessTokenKey, token);
  }

  getToken(): string | null { return this.accessToken; }

  setRefreshToken(token: string): void {
    this.refreshTokenValue = token;
    localStorage.setItem(this.refreshTokenKey, token);
  }

  getRefreshToken(): string | null { return this.refreshTokenValue; }

  clearAll(): void {
    this.accessToken = null;
    this.refreshTokenValue = null;
    this.currentUser = null;
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
    this.clearEmpresaRefHint();
  }

  // Keep for backward compat
  clearToken(): void { this.clearAll(); }

  isTokenExpired(): boolean {
    const token = this.accessToken;
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch { return true; }
  }

  buildAuthHeaders(): HttpHeaders {
    const token = this.getToken();
    let headers = new HttpHeaders();
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }

  setUser(user: AuthUser | null): void {
    this.currentUser = user;
    if (user) localStorage.setItem(this.userKey, JSON.stringify(user));
    else localStorage.removeItem(this.userKey);
  }

  getUser(): AuthUser | null { return this.currentUser; }

  getUserDepartamentos(): Array<{ id: number; nome: string }> {
    return this.currentUser?.departamentos ?? [];
  }

  isGestor(): boolean {
    return (this.currentUser?.perfil ?? '').toUpperCase() === 'GESTOR';
  }

  isColaborador(): boolean {
    return (this.currentUser?.perfil ?? '').toUpperCase() === 'COLABORADOR';
  }

  hasPortalAccess(): boolean {
    return this.currentUser?.acessaPortal === true;
  }
}
